import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

interface GpgPassphraseDialogProps {
  open: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  /** Allow confirming with an empty passphrase (unprotected keys). */
  allowEmpty?: boolean;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (passphrase: string) => void;
}

export default function GpgPassphraseDialog({
  open,
  title = 'GnuPG passphrase',
  description = 'Enter the passphrase for your secret key.',
  confirmLabel = 'Continue',
  allowEmpty = true,
  busy = false,
  error = null,
  onCancel,
  onConfirm,
}: GpgPassphraseDialogProps) {
  const [passphrase, setPassphrase] = useState('');

  useEffect(() => {
    if (open) setPassphrase('');
  }, [open]);

  const canSubmit = allowEmpty || Boolean(passphrase);

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
        {error && (
          <DialogContentText color="error" sx={{ mb: 2 }}>
            {error}
          </DialogContentText>
        )}
        <TextField
          autoFocus
          fullWidth
          type="password"
          label="Passphrase"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit && !busy) onConfirm(passphrase);
          }}
          disabled={busy}
          helperText={allowEmpty ? 'Leave blank if the key has no passphrase.' : undefined}
          inputProps={{ 'aria-label': 'GnuPG passphrase', autoComplete: 'current-password' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit || busy}
          onClick={() => onConfirm(passphrase)}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
